import { useState } from "react";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ALL = "__all";
const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95] focus-visible:ring-offset-2";

// One plain-language filter. Hidden when it would leave nothing (unless already on).
function Toggle({ f }) {
  if (f.hidden) return null;
  return (
    <button type="button" onClick={f.toggle} aria-pressed={f.on} title={f.hint}
      data-testid={"refine-" + f.key}
      className={"inline-flex min-h-10 items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors " + FOCUS + " " +
        (f.on ? "border-[#4C1D95] bg-[#4C1D95] text-white" : "border-stone-300 bg-white text-stone-800 hover:border-[#4C1D95]")}>
      {f.on && <Check className="h-4 w-4" aria-hidden />}
      {f.label}
      <span className={f.on ? "text-white/80" : "text-stone-500"} data-testid={"refine-count-" + f.key}>({f.count})</span>
    </button>
  );
}

function RegionSelect({ regions, value, onChange }) {
  if (!regions.length && !value) return null;
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? null : v)}>
      <SelectTrigger data-testid="refine-region" aria-label="Region"
        className={"h-10 w-auto min-w-[11rem] rounded-full border-stone-300 bg-white px-4 text-sm " + FOCUS + (value ? " border-[#4C1D95] text-[#4C1D95]" : "")}>
        <SelectValue placeholder="Any region" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL} data-testid="refine-region-all">Any region</SelectItem>
        {regions.map((r) => (
          <SelectItem key={r.name} value={r.name} data-testid={"refine-region-" + r.name.replace(/\s+/g, "-").toLowerCase()}>
            {r.label || r.name} ({r.count})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function RefineBar({ main, more, regions, region, onRegion, shown, total, anyActive, onClearAll, extras, moreExtras }) {
  const [open, setOpen] = useState(false);
  const moreVisible = more.filter((f) => !f.hidden);
  const moreOn = more.filter((f) => f.on).length;
  return (
    <div className="w-full" data-testid="refine-bar">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-stone-700">Refine</span>
        <RegionSelect regions={regions} value={region} onChange={onRegion} />
        {main.map((f) => <Toggle key={f.key} f={f} />)}
        {extras}
        {(moreVisible.length > 0 || moreExtras) && (
          <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="refine-more-toggle"
            className={"inline-flex min-h-10 items-center gap-1 text-sm font-medium text-stone-600 hover:text-stone-900 " + FOCUS}>
            More filters{moreOn ? " · " + moreOn + " on" : ""}
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        )}
      </div>
      {open && (
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-stone-200 pt-3" data-testid="refine-more">
          {moreVisible.map((f) => <Toggle key={f.key} f={f} />)}
          {moreExtras}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-stone-500" role="status" aria-live="polite">
        <span data-testid="refine-shown">Showing {shown} of {total}</span>
        {anyActive && (
          <button type="button" onClick={onClearAll} data-testid="refine-clear-all"
            className={"font-semibold text-[#4C1D95] underline-offset-4 hover:underline " + FOCUS}>
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}
